// WorkspaceSidebar: the workspace's
// drawer. What is on the canvas, in the order it was laid on, then the ways to
// find what to lay on next, in the sidebar's own rows: a search over every
// session with the query language, the sessions the trace-review pipeline
// flagged by how bad the worst finding was, the sessions the anchor shares
// content with, and the sessions the chat's investigation names. Picking one
// lays it on the canvas and brings its card into view; picking one already on
// brings it into view.
import { useCallback, useEffect, useMemo, useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import { DimHeader, NavDivider, NavSectionLabel } from "./sidebarPrimitives.jsx";
import { rowChromeClass } from "./SidebarNavRuntime.jsx";
import { useQuery } from "../lib/dataClient.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { buildSessionRows, fetchScopeReviewSessions, mergeSessionPages, reviewsWindow } from "../lib/scopeReviews.js";
import { toApiRange } from "../lib/shell.js";
import { __setSessionTitleLabeler, useSessionTitles } from "../lib/sessionTitles.js";
import {
  addToWorkspace, removeFromWorkspace, requestWorkspaceFocus, useWorkspace, useWorkspaceCanvas,
} from "../lib/workspaceStore.js";
import { activeGraphKey, readLiveGraph, subscribeGraph } from "../lib/investigationGraphRun.js";
import { graphTraceSessions } from "../lib/traceGraphOverlay.js";
import { useDimensionEntry } from "../lib/dimensionDefs.js";

const DOT = " \u00b7 ";
export const SEVERITIES = [["critical", "Critical"], ["high", "High"], ["medium", "Medium"], ["low", "Low"], ["other", "Other"]];
const SHOWN = 8;

// Session titles the lists carry no words for, read in one batch. The harness
// swaps the reader (it has no analytics).
export function __setWorkspaceLabeler(fn) { __setSessionTitleLabeler(fn); }

const whenText = (ms) => (Number.isFinite(ms) ? new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const shortId = (id) => `trace ${String(id).slice(0, 8)}`;

export function MachineName({ id }) {
  const entry = useDimensionEntry(id ? "endpoints" : null, id);
  const label = entry?.label && entry.label !== String(id) ? entry.label : null;
  return label ? <span>{label}</span> : null;
}

function Note({ parts }) {
  const shown = parts.filter(Boolean);
  if (!shown.length) return null;
  return (
    <span className="mt-[3px] block truncate text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">
      {shown.map((p, i) => <span key={i}>{i ? DOT : null}{p}</span>)}
    </span>
  );
}

// A session to lay on: + when it is not on the canvas, a check when it is.
function PickRow({ title, note, meta = null, on, onPick }) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={`relative grid w-full appearance-none [grid-template-columns:14px_minmax(0,1fr)_auto] items-start gap-x-2 border-0 bg-transparent py-[6px] pl-[6px] pr-[9px] text-left ${rowChromeClass(false, true)}`}
      data-workspace-pick=""
    >
      <span className={`mt-[1px] grid h-[14px] place-items-center text-[11px] leading-none ${on ? "text-[color:var(--green)]" : "text-[color:var(--muted-soft)]"}`} aria-hidden="true">
        {on ? "\u2713" : "+"}
      </span>
      <span className="min-w-0">
        <span className="line-clamp-2 break-words text-[12px] leading-[1.2] text-[color:var(--ink)]">{title}</span>
        {note}
      </span>
      {meta != null ? <span className="mt-[1px] whitespace-nowrap text-[11px] leading-[1.2] text-[color:var(--muted)]">{meta}</span> : <span />}
    </button>
  );
}

// A trace on the canvas: its hue (the anchor's diamond), what it was, where it
// ran. Picking it brings its card into view; the x takes it off.
function LayerRow({ title, hue, anchor, machine, loading, onFocus, onRemove }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onFocus}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onFocus(); } }}
      className={`group relative grid w-full cursor-pointer [grid-template-columns:14px_minmax(0,1fr)_16px] items-start gap-x-2 py-[6px] pl-[6px] pr-[9px] text-left ${rowChromeClass(false, true)}`}
      data-workspace-layer=""
    >
      <span className="mt-[2px] grid h-[12px] place-items-center" aria-hidden="true">
        {anchor ? (
          <svg viewBox="0 0 10 10" width="9" height="9"><path d="M5 0.5 9.5 5 5 9.5 0.5 5Z" fill="var(--ink)" /></svg>
        ) : (
          <span className="block h-[8px] w-[8px] rounded-full" style={{ background: hue || "var(--muted-soft)" }} />
        )}
      </span>
      <span className="min-w-0">
        <span className="line-clamp-2 break-words text-[12px] font-medium leading-[1.2] text-[color:var(--ink)]">{title}</span>
        <Note parts={[anchor ? "anchor" : null, machine, loading ? "reading..." : null]} />
      </span>
      <HoverTooltip placement="right" label="Take it off the canvas">
        <button
          type="button"
          aria-label="Take it off the canvas"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="grid h-[14px] w-[14px] place-items-center text-[color:var(--muted-soft)] opacity-0 hover:text-[color:var(--ink)] focus:opacity-100 group-hover:opacity-100"
        >
          <svg viewBox="0 0 12 12" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
        </button>
      </HoverTooltip>
    </div>
  );
}

// "+k more" under a list, the sidebar's way.
function MoreRow({ count, open, onToggle }) {
  if (!count && !open) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full border-0 bg-transparent py-[4px] pl-[28px] pr-[9px] text-left text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)] ${rowChromeClass(false, true)}`}
    >
      {open ? "Show fewer" : `+${count} more`}
    </button>
  );
}

function Quiet({ children }) {
  return <div className="py-[4px] pl-[28px] pr-[9px] text-[11px] leading-[1.35] text-[color:var(--muted-soft)]">{children}</div>;
}

// The sessions with findings in the drawer's range, the Reviews lens's read.
// Read once per range and kept a minute for every reader of it (the
// sidebar's Triage list and the panes' fleet ask at the same time). Without
// their prompts' summaries (their newest prompt, a heavy read per session):
// a list showing them titles the ones it shows (getSessionHeads).
const FOUND_KEEP_MS = 60_000;
const foundReads = new Map();
const FINDING_LOADING = { status: "loading", sessions: [] };
export function useFindingSessions(range) {
  const caps = useMyPermissions();
  const sensitive = hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
  const rangeKey = typeof range === "string" ? range : JSON.stringify(range ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const win = useMemo(() => reviewsWindow(toApiRange(range)), [rangeKey]);
  // Keyed by the range as picked (see useFleetFindings): its bounds move
  // with the clock between readers asking a moment apart.
  const key = `${rangeKey}|${sensitive ? 1 : 0}`;
  const [state, setState] = useState(null);
  useEffect(() => {
    let live = true;
    const hit = foundReads.get(key);
    const entry = hit && Date.now() - hit.at < FOUND_KEEP_MS ? hit : {
      at: Date.now(),
      promise: fetchScopeReviewSessions({ sessionIds: null, fromMs: win.fromMs, toMs: win.toMs, sensitive, previews: false, offset: 0, signal: null })
        .then((page) => buildSessionRows(mergeSessionPages([page])).sessions),
    };
    foundReads.set(key, entry);
    entry.promise.then(
      (sessions) => {
        // Nothing read is not kept: it may be a read made before the session
        // was signed in.
        if (!sessions.length) foundReads.delete(key);
        if (live) setState({ key, status: "done", sessions });
      },
      (error) => { foundReads.delete(key); if (live) setState({ key, status: "error", sessions: [], error }); },
    );
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  // Another range's sessions are not this one's: until its own are read, it
  // is loading.
  return state?.key === key ? state : FINDING_LOADING;
}

// The active chat's investigation graph, re-read as it grows.
export function useChatSessions() {
  const [key, setKey] = useState(() => activeGraphKey());
  useEffect(() => {
    const t = setInterval(() => setKey((cur) => (activeGraphKey() === cur ? cur : activeGraphKey())), 1500);
    return () => clearInterval(t);
  }, []);
  const [version, setVersion] = useState(0);
  useEffect(() => (key ? subscribeGraph(key, () => setVersion((v) => v + 1)) : undefined), [key]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => graphTraceSessions(key ? readLiveGraph(key) : null), [key, version]);
}

// Titles for sessions a list names only by id, fetched once per id.
export const useTitles = useSessionTitles;

function SearchSection({ range, on, pick }) {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setQ(text.trim()), 400);
    return () => clearTimeout(t);
  }, [text]);
  const { data, loading, error } = useQuery("getSessionLog", { scopePath: [], range, query: q, sort: "newest", limit: 40, byTrace: true }, { enabled: !!q });
  const rows = q && Array.isArray(data) ? data : [];
  return (
    <>
      <div className="px-[6px] pb-[4px] pr-[9px]">
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Search, e.g. agent:codex file:deploy"
          aria-label="Search traces"
          className="h-[26px] w-full rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[7px] font-mono text-[11px] text-[color:var(--ink)] outline-none placeholder:font-sans placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--line-strong)]"
          data-workspace-search=""
        />
      </div>
      {q ? (
        loading && !rows.length ? <Quiet>Searching...</Quiet>
          : error ? <Quiet>The search failed.</Quiet>
            : !rows.length ? <Quiet>No traces match in this range.</Quiet>
              : (
                <>
                  {(all ? rows : rows.slice(0, SHOWN)).map((row) => {
                    const id = String(row.sessionId || row.id || "").toLowerCase();
                    const title = String(row.summary || "").replace(/\s+/g, " ").trim() || shortId(id);
                    return (
                      <PickRow
                        key={id}
                        title={title}
                        on={on.has(id)}
                        onPick={() => pick(id, title)}
                        note={<Note parts={[whenText(row.lastTimestamp ?? row.timestamp), row.endpointId ? <MachineName id={row.endpointId} /> : null]} />}
                      />
                    );
                  })}
                  <MoreRow count={rows.length - SHOWN > 0 ? rows.length - SHOWN : 0} open={all} onToggle={() => setAll((v) => !v)} />
                </>
              )
      ) : null}
    </>
  );
}

function FindingsSection({ range, on, pick }) {
  const found = useFindingSessions(range);
  const groups = useMemo(() => {
    const by = new Map(SEVERITIES.map(([id]) => [id, []]));
    for (const s of found.sessions) {
      const sev = SEVERITIES.some(([id]) => id === s.worstBehaviour) ? s.worstBehaviour : "other";
      by.get(sev).push(s);
    }
    return SEVERITIES.map(([id, label]) => ({ id, label, sessions: by.get(id) })).filter((g) => g.sessions.length);
  }, [found.sessions]);
  const [open, setOpen] = useState(() => new Set());
  const [full, setFull] = useState(() => new Set());
  // The worst group opens by itself once the read lands; the reader's own
  // opening and closing wins after that.
  const [touched, setTouched] = useState(false);
  const openNow = touched ? open : new Set(groups.slice(0, 1).map((g) => g.id));
  const flip = (set, id) => { const next = new Set(set); if (next.has(id)) next.delete(id); else next.add(id); return next; };
  if (found.status === "loading") return <Quiet>Reading the reviews...</Quiet>;
  if (found.status === "error") return <Quiet>The reviews could not be read.</Quiet>;
  if (!groups.length) return <Quiet>No findings in this range.</Quiet>;
  return groups.map((g) => {
    const isOpen = openNow.has(g.id);
    const all = full.has(g.id);
    return (
      <div key={g.id} data-workspace-severity={g.id}>
        <DimHeader label={g.label} count={g.sessions.length} expanded={isOpen} onClick={() => { setTouched(true); setOpen(flip(openNow, g.id)); }} />
        {isOpen ? (
          <>
            {(all ? g.sessions : g.sessions.slice(0, SHOWN)).map((s) => (
              <PickRow
                key={s.id}
                title={s.summary || shortId(s.id)}
                on={on.has(s.id)}
                onPick={() => pick(s.id, s.summary || "")}
                meta={s.findings ? String(s.findings) : null}
                note={<Note parts={[whenText(s.latestAt), s.endpointId ? <MachineName id={s.endpointId} /> : null]} />}
              />
            ))}
            <MoreRow count={g.sessions.length > SHOWN ? g.sessions.length - SHOWN : 0} open={all} onToggle={() => setFull(flip(full, g.id))} />
          </>
        ) : null}
      </div>
    );
  });
}

export default function WorkspaceSidebar({ range }) {
  const ws = useWorkspace();
  const canvas = useWorkspaceCanvas();
  const chat = useChatSessions();
  const on = useMemo(() => new Set(ws.traces.map((t) => t.id)), [ws.traces]);
  const pick = useCallback((id, title) => {
    const key = String(id || "").toLowerCase();
    if (!key) return;
    if (!on.has(key)) addToWorkspace({ id: key, title: title || "" });
    requestWorkspaceFocus(key);
  }, [on]);
  const [relatedAll, setRelatedAll] = useState(false);
  const [chatAll, setChatAll] = useState(false);

  const cardOf = useMemo(() => new Map((canvas?.cards || []).map((c) => [c.id, c])), [canvas?.cards]);
  const related = useMemo(() => (canvas?.related || []).filter((r) => !r.kgOnly).sort((a, b) => b.evidence - a.evidence || b.shared - a.shared), [canvas?.related]);
  const relatedShown = relatedAll ? related.slice(0, 40) : related.slice(0, SHOWN);
  const chatShown = chatAll ? chat : chat.slice(0, SHOWN);
  const span = useMemo(() => {
    const ts = related.flatMap((r) => [r.firstMs, r.lastMs]).filter(Number.isFinite);
    return ts.length ? { from: Math.min(...ts), to: Math.max(...ts) } : null;
  }, [related]);
  const titled = useMemo(() => new Map(ws.traces.filter((t) => t.title).map((t) => [t.id, t.title])), [ws.traces]);
  const needTitles = useMemo(() => [...new Set([...relatedShown.map((r) => r.id), ...chatShown.map((c) => String(c.id).toLowerCase())])].filter((id) => !titled.has(id)), [relatedShown, chatShown, titled]);
  const titles = useTitles(needTitles, span);
  const titleOf = (id) => titled.get(id) || titles.get(id) || cardOf.get(id)?.title || shortId(id);

  return (
    <nav className="flex flex-col py-1 pb-3" data-workspace-sidebar="">
      <NavSectionLabel>On the canvas</NavSectionLabel>
      {ws.traces.length ? ws.traces.map((t) => {
        const card = cardOf.get(t.id);
        return (
          <LayerRow
            key={t.id}
            title={t.title || card?.title || shortId(t.id)}
            hue={card?.hue}
            anchor={t.id === ws.anchor}
            machine={card?.machine || null}
            loading={card ? card.status === "loading" : t.id !== ws.anchor}
            onFocus={() => requestWorkspaceFocus(t.id)}
            onRemove={() => removeFromWorkspace(t.id)}
          />
        );
      }) : <Quiet>Nothing yet. Pick a trace below to lay it on the canvas.</Quiet>}

      <NavDivider />
      <NavSectionLabel>Find</NavSectionLabel>
      <SearchSection range={range} on={on} pick={pick} />

      <NavDivider />
      <NavSectionLabel>Findings</NavSectionLabel>
      <FindingsSection range={range} on={on} pick={pick} />

      {ws.anchor ? (
        <>
          <NavDivider />
          <NavSectionLabel>Related to the anchor</NavSectionLabel>
          {!canvas || canvas.status === "loading" || canvas.status === "idle" ? <Quiet>Searching for the traces it shares content with...</Quiet>
            : canvas.status === "error" ? <Quiet>The search for related traces failed.</Quiet>
              : !related.length ? <Quiet>No other trace shares its content.</Quiet>
                : (
                  <>
                    {relatedShown.map((r) => (
                      <PickRow
                        key={r.id}
                        title={titleOf(r.id)}
                        on={on.has(r.id)}
                        onPick={() => pick(r.id, titles.get(r.id) || "")}
                        note={<Note parts={[r.shared ? `${r.shared} shared` : null, whenText(r.firstMs), r.endpointId ? <MachineName id={r.endpointId} /> : null]} />}
                      />
                    ))}
                    <MoreRow count={related.length > SHOWN ? Math.min(40, related.length) - SHOWN : 0} open={relatedAll} onToggle={() => setRelatedAll((v) => !v)} />
                  </>
                )}
        </>
      ) : null}

      {chat.length ? (
        <>
          <NavDivider />
          <NavSectionLabel>In the chat</NavSectionLabel>
          {chatShown.map((c) => {
            const id = String(c.id).toLowerCase();
            return (
              <PickRow
                key={id}
                title={titleOf(id)}
                on={on.has(id)}
                onPick={() => pick(id, titles.get(id) || "")}
                note={<Note parts={[c.claims ? `${c.claims} claim${c.claims === 1 ? "" : "s"}` : "named in the investigation"]} />}
              />
            );
          })}
          <MoreRow count={chat.length > SHOWN ? chat.length - SHOWN : 0} open={chatAll} onToggle={() => setChatAll((v) => !v)} />
        </>
      ) : null}
    </nav>
  );
}
